import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';
// Parse Facebook/Instagram Graph API date strings (ISO 8601)
export const parseFbDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

// -----------------------------------------------

const initialState = {
    isLoading: false,
    boxLoading: false,
    instagramAccounts: '',
    instagramAccountsPages: '',
    instagramAccountsPosts: {
        results: []
    },
    instagramPostComments: [],
};

const slice = createSlice({
    name: 'instagram',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        screenLoading(state, action) {
            state.boxLoading = action.payload;
        },
        setInstagramAccounts(state, action) {
            state.isLoading = false;
            state.instagramAccounts = action.payload;
        },
        setInstagramAccountsPages(state, action) {
            state.isLoading = false;
            state.instagramAccountsPages = action.payload;
        },
        setInstagramAccountsPosts(state, action) {
            state.isLoading = false;
            state.instagramAccountsPosts = action.payload;
        },
        setInstagramPostComments(state, action) {
            state.isLoading = false;
            state.instagramPostComments = action.payload;
        },
        addOptimisticComment(state, action) {
            if (state.instagramPostComments?.results) {
                state.instagramPostComments.results = [...state.instagramPostComments.results, action.payload];
            }
        },
    }
});

export default slice.reducer;

export const {
    reset,
    startLoading,
    screenLoading,
    setInstagramAccounts,
    setInstagramAccountsPages,
    setInstagramAccountsPosts,
    setInstagramPostComments,
    addOptimisticComment,
} = slice.actions;

// ─── Thunks ───

export function getInstagramAccounts(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.setInstagramAccounts(response.data));
            } else {
                dispatch(slice.actions.setInstagramAccounts({ results: [] }));
            }
        } catch (error) {
            dispatch(slice.actions.setInstagramAccounts({ results: [] }));
        }
    };
}

export function getInstagramAccountsPages(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/`);
            if (response.status === 200) {
                dispatch(slice.actions.setInstagramAccountsPages(response.data));
                dispatch(screenLoading(false));
            } else {
                dispatch(slice.actions.setInstagramAccountsPages({ results: [] }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(slice.actions.setInstagramAccountsPages({ results: [] }));
            dispatch(screenLoading(false));
        }
    };
}

export function getInstagramAccountsPosts(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/?ordering=-created_time`);
            if (response.status === 200) {
                dispatch(slice.actions.setInstagramAccountsPosts(response.data));
                dispatch(screenLoading(false));
            } else {
                dispatch(slice.actions.setInstagramAccountsPosts({ results: [] }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(slice.actions.setInstagramAccountsPosts({ results: [] }));
            dispatch(screenLoading(false));
        }
    };
}

export function getInstagramPostComments(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/${data?.pagePostId}/instagram-post-comments/`);
            if (response.status === 200) {
                dispatch(slice.actions.setInstagramPostComments(response.data));
                dispatch(screenLoading(false));
            } else {
                dispatch(slice.actions.setInstagramPostComments({ results: [] }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(slice.actions.setInstagramPostComments({ results: [] }));
            dispatch(screenLoading(false));
        }
    };
}

export function addInstagramPostComment(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/${data?.pagePostId}/add_comment/`, 'post', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment added' }));
                dispatch(screenLoading(false));
                dispatch(getInstagramPostComments(data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(screenLoading(false));
        }
    };
}

export function deleteInstagramPostComment(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/${data?.pagePostId}/instagram-post-comments/${data?.comment_id}/delete/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment deleted' }));
                dispatch(screenLoading(false));
                if (handleClose) handleClose({ data });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(screenLoading(false));
        }
    };
}

export function hideInstagramPostComment(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/${data?.pagePostId}/instagram-post-comments/${data?.comment_id}/hide/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment hidden' }));
                dispatch(screenLoading(false));
                if (handleClose) handleClose({ data });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(screenLoading(false));
        }
    };
}

export function addInstagramCommentReply(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/${data?.pagePostId}/instagram-post-comments/${data?.comment_id}/reply/`, 'post', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Reply added' }));
                dispatch(screenLoading(false));
                dispatch(getInstagramPostComments(data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(screenLoading(false));
        }
    };
}

export function editInstagramPostComment(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/instagram-accounts/${data?.accountId}/instagram-pages/${data?.pageId}/instagram-page-posts/${data?.pagePostId}/instagram-post-comments/${data?.comment_id}/`, 'put', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment updated' }));
                dispatch(screenLoading(false));
                dispatch(getInstagramPostComments(data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                dispatch(screenLoading(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(screenLoading(false));
        }
    };
}
